import { computed, ref } from "vue"
import { getRecentSearches, saveRecentSearch } from "./useRecentSearches"
import { getRecentItems, mergeRecentItems, saveRecentItem } from "./useRecentItems"
import { flattenGrouped, globalItemIndex, groupByType } from "../utils/search_grouping"

const MIN_LENGTH = 2
const DEBOUNCE_MS = 150

export function useSearchAutocomplete(props) {
  const query = ref("")
  const results = ref([])
  const groupedResults = ref({})
  const recentSearches = ref([])
  const recentItems = ref([])
  const selectedIndex = ref(-1)
  const isOpen = ref(false)
  const isLoading = ref(false)
  const isLoadingRecent = ref(false)
  const errorMessage = ref("")
  const showEmptyState = ref(false)
  const flatItems = ref([])

  let debounceTimer = null
  let blurTimer = null

  const label = (key, fallback) => props.labels?.[key] || fallback

  const hasResults = computed(() => results.value.length > 0)
  const hasRecentItems = computed(() => recentItems.value.length > 0)
  const hasRecentSearches = computed(() => recentSearches.value.length > 0)
  const showEmpty = computed(
    () =>
      isOpen.value &&
      !isLoading.value &&
      !isLoadingRecent.value &&
      !errorMessage.value &&
      !showEmptyState.value &&
      !hasResults.value &&
      query.value.trim().length >= MIN_LENGTH
  )
  const showDropdown = computed(
    () =>
      isOpen.value &&
      (isLoading.value ||
        isLoadingRecent.value ||
        errorMessage.value ||
        showEmptyState.value ||
        hasResults.value ||
        showEmpty.value)
  )

  function openDropdown() {
    isOpen.value = true
  }

  function closeDropdown() {
    isOpen.value = false
    selectedIndex.value = -1
    flatItems.value = []
  }

  function buildEmptyStateFlatItems() {
    const items = recentItems.value.map((item) => ({ kind: "item", value: item }))
    const terms = recentSearches.value.map((term) => ({ kind: "term", value: term }))
    flatItems.value = [...items, ...terms]
    selectedIndex.value = -1
  }

  async function fetchRecentItems() {
    if (!props.recentUrl) return []

    const url = new URL(props.recentUrl, window.location.origin)
    url.searchParams.set("limit", "8")

    const response = await fetch(url, {
      headers: { Accept: "application/json" },
    })

    if (!response.ok) throw new Error("Recent items failed")

    return response.json()
  }

  async function showEmptyStateDropdown() {
    recentSearches.value = getRecentSearches()
    showEmptyState.value = false
    results.value = []
    groupedResults.value = {}
    errorMessage.value = ""
    isLoadingRecent.value = true
    openDropdown()

    try {
      const serverItems = await fetchRecentItems()
      recentItems.value = mergeRecentItems(serverItems, getRecentItems())
    } catch (error) {
      console.error("Recent items error:", error)
      recentItems.value = getRecentItems()
    } finally {
      isLoadingRecent.value = false
    }

    const hasEmptyContent = recentItems.value.length > 0 || recentSearches.value.length > 0
    showEmptyState.value = hasEmptyContent

    if (!hasEmptyContent) {
      closeDropdown()
      return
    }

    buildEmptyStateFlatItems()
  }

  async function performSearch(searchQuery) {
    isLoading.value = true
    errorMessage.value = ""
    showEmptyState.value = false
    openDropdown()

    try {
      const url = new URL(props.url, window.location.origin)
      url.searchParams.set("q", searchQuery)
      url.searchParams.set("limit", "8")
      url.searchParams.set("format", "json")

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      })

      if (!response.ok) throw new Error("Search failed")

      const data = await response.json()
      results.value = data
      groupedResults.value = groupByType(data)
      flatItems.value = flattenGrouped(groupedResults.value).map((item) => ({
        kind: "item",
        value: item,
      }))
      selectedIndex.value = -1
    } catch (error) {
      console.error("Search error:", error)
      results.value = []
      groupedResults.value = {}
      flatItems.value = []
      errorMessage.value = label("error", "Search failed")
    } finally {
      isLoading.value = false
    }
  }

  function scheduleSearch() {
    const trimmed = query.value.trim()

    if (trimmed.length < MIN_LENGTH) {
      showEmptyStateDropdown()
      return
    }

    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => performSearch(trimmed), DEBOUNCE_MS)
  }

  function onFocus() {
    if (query.value.trim().length >= MIN_LENGTH) {
      scheduleSearch()
    } else {
      showEmptyStateDropdown()
    }
  }

  function onBlur() {
    blurTimer = setTimeout(() => closeDropdown(), 200)
  }

  function itemDomId(index) {
    return `search-item-${index}`
  }

  function updateSelection() {
    if (selectedIndex.value < 0) return
    const element = document.getElementById(itemDomId(selectedIndex.value))
    element?.scrollIntoView({ block: "nearest" })
  }

  function selectItem(index) {
    selectedIndex.value = index
    updateSelection()
  }

  function dispatchPreview(item) {
    const host = props.hostElement || document.querySelector(".search-autocomplete")
    if (!host) return

    host.dispatchEvent(
      new CustomEvent("search-autocomplete:preview", {
        bubbles: true,
        detail: { id: item.id, url: item.url },
      })
    )
  }

  function rememberItem(item) {
    saveRecentItem(item)
  }

  function navigateToItem(item) {
    if (query.value.trim().length >= MIN_LENGTH) {
      saveRecentSearch(query.value)
    }
    rememberItem(item)
    closeDropdown()
    window.location.href = item.url
  }

  function openPreview(item) {
    if (query.value.trim().length >= MIN_LENGTH) {
      saveRecentSearch(query.value)
    }
    rememberItem(item)
    closeDropdown()
    dispatchPreview(item)
  }

  function handleItemClick(item) {
    if (props.adminMode) {
      openPreview(item)
    } else {
      navigateToItem(item)
    }
  }

  function useRecentSearch(term) {
    query.value = term
    scheduleSearch()
  }

  function onKeydown(event) {
    if (!showDropdown.value) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (flatItems.value.length > 0) {
          selectedIndex.value = Math.min(selectedIndex.value + 1, flatItems.value.length - 1)
          updateSelection()
        }
        break
      case "ArrowUp":
        event.preventDefault()
        selectedIndex.value = Math.max(selectedIndex.value - 1, -1)
        updateSelection()
        break
      case "Enter":
        if (selectedIndex.value >= 0 && flatItems.value[selectedIndex.value]) {
          event.preventDefault()
          const entry = flatItems.value[selectedIndex.value]
          if (entry.kind === "term") {
            useRecentSearch(entry.value)
          } else {
            handleItemClick(entry.value)
          }
        }
        break
      case "Escape":
        closeDropdown()
        event.target.blur()
        break
    }
  }

  function groupedEntries() {
    return Object.entries(groupedResults.value)
  }

  function resolveGlobalItemIndex(groupIndex, itemIndex) {
    return globalItemIndex(groupedResults.value, groupIndex, itemIndex)
  }

  function recentItemIndex(itemIndex) {
    return itemIndex
  }

  function recentSearchIndex(termIndex) {
    return recentItems.value.length + termIndex
  }

  function cleanup() {
    clearTimeout(debounceTimer)
    clearTimeout(blurTimer)
  }

  return {
    query,
    recentSearches,
    recentItems,
    selectedIndex,
    isLoading,
    isLoadingRecent,
    errorMessage,
    showEmptyState,
    showDropdown,
    showEmpty,
    label,
    scheduleSearch,
    onFocus,
    onBlur,
    onKeydown,
    selectItem,
    handleItemClick,
    useRecentSearch,
    groupedEntries,
    itemDomId,
    resolveGlobalItemIndex,
    recentItemIndex,
    recentSearchIndex,
    cleanup,
  }
}
